import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { auth, db } from '../services/firebase';
import { collection, onSnapshot, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Box, Typography, Card, CardContent, TextField, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from '../styles/Dashboard.module.css';

const GerenciarMoradores = () => {
  const [moradores, setMoradores] = useState([]);
  const [novoMorador, setNovoMorador] = useState({ nome: '', email: '', apto: '', papel: 'morador' });
  const [editarMorador, setEditarMorador] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [foto, setFoto] = useState(null);
  const [editFoto, setEditFoto] = useState(null); // Estado para a foto na edição
  const [visualizarFoto, setVisualizarFoto] = useState({ open: false, url: '', nome: '' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        // Busca o condomínio do usuário logado no Firestore
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        } else {
          console.error('Erro: Dados do usuário não encontrados no Firestore.');
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!condominioId) return; // Não busca moradores se condominioId não estiver carregado

    // CORREÇÃO: Adicionado filtro para papel: 'morador' e usando onSnapshot para tempo real.
    const q = query(
      collection(db, 'tb_usuarios'),
      where('condominio_id', '==', condominioId),
      where('papel', 'in', ['morador', 'sindico', 'sub_sindico', 'conselheiro'])
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const moradoresList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMoradores(moradoresList);
    });

    return () => unsubscribe(); // Limpa o listener ao desmontar o componente
  }, [condominioId]);

  // Adicionar um novo morador
  const handleAdicionarMorador = async (e) => {
    e.preventDefault();

    // A verificação de permissão já é feita pelo ProtectedRoute e pelo backend.
    // Aqui vamos chamar um endpoint no backend para criar o usuário.
    try {
      let foto_url = '';
      let foto_path = '';

      // Se uma foto foi selecionada, faz o upload antes de criar o usuário
      if (foto) {
        const storage = getStorage();
        // Cria um caminho único para o arquivo para evitar sobreposições
        const fotoPathTemp = `moradores/${condominioId}/temp_${Date.now()}_${foto.name}`;
        const fotoRef = ref(storage, fotoPathTemp);

        const metadata = {
          customMetadata: {
            'userRole': userRole,
            'condominioId': condominioId,
            'ownerId': currentUser.uid
          }
        };

        const snapshot = await uploadBytes(fotoRef, foto, metadata);
        foto_url = await getDownloadURL(snapshot.ref);
        foto_path = fotoPathTemp; // Salva o caminho temporário
      }

      const token = await currentUser.getIdToken();

      // ... (inside handleAdicionarMorador)
      const response = await fetch(`${getApiUrl()}/api/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: novoMorador.email,
          nome: novoMorador.nome,
          apto: novoMorador.apto,
          condominio_id: condominioId,
          foto_url, // Envia a URL da foto
          foto_path, // Envia o caminho da foto
          papel: novoMorador.papel,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar usuário.');
      }

      const newUser = await response.json();

      // Limpa o formulário. O onSnapshot cuidará de atualizar a lista.
      setNovoMorador({ nome: '', email: '', apto: '', papel: 'morador' });
      setFoto(null);
      alert('Usuário criado com sucesso! Um email para definição de senha foi enviado.');
    } catch (error) {
      alert('Erro ao adicionar morador: ' + error.message);
    }
  };

  // Editar um morador existente
  const handleEditarMorador = async (e) => {
    e.preventDefault();
    if (!editarMorador) return;

    // Mostra um alerta de carregamento
    alert('Atualizando dados, por favor aguarde...');

    try {
      let foto_url = editarMorador.foto_url;
      let foto_path = editarMorador.foto_path;

      // Se uma nova foto foi selecionada
      if (editFoto) {
        const storage = getStorage();
        // 1. Deleta a foto antiga via backend
        if (editarMorador.foto_path) {
          const token = await currentUser.getIdToken();
          await fetch(`${getApiUrl()}/api/delete-old-morador-foto`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ foto_path: editarMorador.foto_path }),
          });
        }

        // 2. Faz upload da nova foto
        const newFotoPath = `moradores/${condominioId}/${editarMorador.id}_${Date.now()}_${editFoto.name}`;
        const newFotoRef = ref(storage, newFotoPath);
        const metadata = {
          customMetadata: {
            'userRole': userRole,
            'condominioId': condominioId,
            'ownerId': currentUser.uid
          }
        };
        const snapshot = await uploadBytes(newFotoRef, editFoto, metadata);
        foto_url = await getDownloadURL(snapshot.ref);
        foto_path = newFotoPath;
      }

      // 3. Atualiza o documento no Firestore
      const moradorDocRef = doc(db, 'tb_usuarios', editarMorador.id);
      await updateDoc(moradorDocRef, {
        nome: editarMorador.nome,
        apto: editarMorador.apto,
        foto_url: foto_url,
        foto_path: foto_path,
        papel: editarMorador.papel || 'morador',
      });

      // O onSnapshot já vai atualizar a lista, então só precisamos fechar o modal
      setEditarMorador(null);
      setEditFoto(null);
      alert('Morador atualizado com sucesso!');
    } catch (error) {
      alert('Erro ao atualizar morador: ' + error.message);
    }
  };

  // Remover um morador (requer um endpoint no backend para deletar do Auth e Firestore)
  const handleRemoverMorador = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover este morador? Esta ação não pode ser desfeita.")) {
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/delete-user/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao remover usuário.');
      }

      // Remove da lista local
      setMoradores(moradores.filter(m => m.id !== id));
      alert('Morador removido com sucesso!');
    } catch (error) {
      alert('Erro ao remover morador: ' + error.message);
    }
  };

  const handleAbrirFoto = (morador) => {
    if (morador.foto_url) {
      setVisualizarFoto({ open: true, url: morador.foto_url, nome: morador.nome });
    }
  };

  const handleFecharFoto = () => {
    setVisualizarFoto({ open: false, url: '', nome: '' });
  };


  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Gerenciar Moradores</Typography>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Adicionar Novo Morador</Typography>
            <Grid container spacing={2} component="form" onSubmit={handleAdicionarMorador} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Nome" value={novoMorador.nome} onChange={(e) => setNovoMorador({ ...novoMorador, nome: e.target.value })} required variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Email" type="email" value={novoMorador.email} onChange={(e) => setNovoMorador({ ...novoMorador, email: e.target.value })} required variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth label="Apartamento" value={novoMorador.apto} onChange={(e) => setNovoMorador({ ...novoMorador, apto: e.target.value })} required variant="outlined" size="small" />
              </Grid>
              {userRole === 'sindico' && (
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Função</InputLabel>
                    <Select
                      value={novoMorador.papel}
                      label="Função"
                      onChange={(e) => setNovoMorador({ ...novoMorador, papel: e.target.value })}
                    >
                      <MenuItem value="morador">Morador</MenuItem>
                      <MenuItem value="sub_sindico">Sub-Síndico</MenuItem>
                      <MenuItem value="conselheiro">Conselheiro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={2}>
                {!foto ? (
                  <Button variant="outlined" component="label" fullWidth>
                    Anexar Foto
                    <input type="file" hidden accept="image/*" onChange={(e) => setFoto(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip label={foto.name} onDelete={() => setFoto(null)} />
                )}
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button type="submit" variant="contained" fullWidth>Adicionar</Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Lista de Moradores</Typography>
            <List>
              {moradores && moradores.length > 0 ? (
                moradores.map((morador) => (
                  <ListItem
                    key={morador.id}
                    divider
                    secondaryAction={
                      <>
                        <IconButton edge="end" aria-label="edit" onClick={() => setEditarMorador(morador)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleRemoverMorador(morador.id)} sx={{ ml: 1 }}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={morador.foto_url}
                        onClick={() => handleAbrirFoto(morador)}
                        sx={{ cursor: morador.foto_url ? 'pointer' : 'default' }}
                      >
                        {!morador.foto_url && <PersonIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={morador.nome}
                      secondary={`Email: ${morador.email} | Apto: ${morador.apto}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography sx={{ p: 2 }}>Nenhum morador encontrado.</Typography>
              )}
            </List>
          </CardContent>
        </Card>

        {/* Modal de Edição */}
        {editarMorador && (
          <Dialog open={!!editarMorador} onClose={() => setEditarMorador(null)} PaperProps={{ component: 'form', onSubmit: handleEditarMorador }}>
            <DialogTitle>Editar Morador</DialogTitle>
            <DialogContent>
              <TextField autoFocus margin="dense" label="Nome" type="text" fullWidth variant="standard" value={editarMorador.nome} onChange={(e) => setEditarMorador({ ...editarMorador, nome: e.target.value })} required />
              <TextField margin="dense" label="Apartamento" type="text" fullWidth variant="standard" value={editarMorador.apto} onChange={(e) => setEditarMorador({ ...editarMorador, apto: e.target.value })} required />

              {userRole === 'sindico' && (
                <FormControl fullWidth margin="dense">
                  <InputLabel id="edit-role-label">Função</InputLabel>
                  <Select
                    labelId="edit-role-label"
                    value={editarMorador.papel || 'morador'}
                    label="Função"
                    onChange={(e) => setEditarMorador({ ...editarMorador, papel: e.target.value })}
                  >
                    <MenuItem value="morador">Morador</MenuItem>
                    <MenuItem value="sub_sindico">Sub-Síndico</MenuItem>
                    <MenuItem value="conselheiro">Conselheiro</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Box sx={{ mt: 2 }}>
                {!editFoto ? (
                  <Button variant="outlined" component="label" fullWidth>
                    Alterar Foto
                    <input type="file" hidden accept="image/*" onChange={(e) => setEditFoto(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip label={editFoto.name} onDelete={() => setEditFoto(null)} />
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditarMorador(null)}>Cancelar</Button>
              <Button type="submit" variant="contained">Salvar</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Modal de Visualização de Foto */}
        <Dialog open={visualizarFoto.open} onClose={handleFecharFoto}>
          <DialogTitle>{visualizarFoto.nome}</DialogTitle>
          <DialogContent>
            <img src={visualizarFoto.url} alt={`Foto de ${visualizarFoto.nome}`} style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFecharFoto}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default GerenciarMoradores;